import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  ChevronLeft, Trophy, Users, Calendar, MessageCircle, Image, Video,
  Play, Clock, MapPin, Send, CheckCircle2, XCircle, HelpCircle,
  Megaphone, Star, AlertTriangle, Layers, Shield, Sparkles, Hash,
  Film, Camera, Upload, Loader2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_STYLES = {
  Gold: 'text-amber-400 bg-amber-500/10 border-amber-700',
  Silver: 'text-zinc-300 bg-zinc-400/10 border-zinc-600',
  Bronze: 'text-orange-400 bg-orange-500/10 border-orange-700',
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const formatTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};
const formatDay = (iso) => {
  if (!iso) return { day: '', month: '', weekday: '' };
  const d = new Date(iso);
  return { day: d.getDate(), month: d.toLocaleDateString('en-US', { month: 'short' }), weekday: d.toLocaleDateString('en-US', { weekday: 'short' }) };
};

const TeamHQ = () => {
  const { slug, teamSlug } = useParams();
  const { token, user } = useAuth();
  const [program, setProgram] = useState(null);
  const [hqData, setHqData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [messages, setMessages] = useState([]);
  const [chatMsg, setChatMsg] = useState('');
  const [myRole, setMyRole] = useState(null);
  const [isBroadcast, setIsBroadcast] = useState(false);
  const [rsvpSummaries, setRsvpSummaries] = useState({});

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const isCoachOrAdmin = myRole && ['program_admin', 'super_admin', 'coach'].includes(myRole);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const progRes = await fetch(`${API_URL}/api/programs/by-slug/${slug}`);
        if (!progRes.ok) { setLoading(false); return; }
        const prog = await progRes.json();
        setProgram(prog);

        const hqRes = await fetch(`${API_URL}/api/programs/${prog.id}/teams/${teamSlug}/hq`);
        if (hqRes.ok) setHqData(await hqRes.json());

        // Fetch messages
        const msgRes = await fetch(`${API_URL}/api/programs/${prog.id}/messages?team_id=${teamSlug}&limit=50`, { headers });
        if (msgRes.ok) setMessages(await msgRes.json());

        // Check role
        if (token) {
          try {
            const myProgs = await fetch(`${API_URL}/api/programs/my-programs`, { headers }).then(r => r.json());
            const mine = myProgs.find(p => p.id === prog.id);
            setMyRole(mine?.my_role || (['admin', 'super_admin'].includes(user?.role) ? 'super_admin' : null));
          } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [slug, teamSlug, token]);

  // Fetch RSVP summaries when schedule tab is active and user is coach/admin
  useEffect(() => {
    if (activeTab !== 'schedule' || !program || !hqData || !isCoachOrAdmin) return;
    const fetchSummaries = async () => {
      const events = [...(hqData.schedule_upcoming || [])];
      const summaries = {};
      for (const e of events) {
        try {
          const res = await fetch(`${API_URL}/api/programs/${program.id}/schedule/${e.id}/rsvp-summary`);
          if (res.ok) summaries[e.id] = await res.json();
        } catch { /* ignore */ }
      }
      setRsvpSummaries(summaries);
    };
    fetchSummaries();
  }, [activeTab, program, hqData, isCoachOrAdmin]);

  const sendMessage = async (broadcast = false) => {
    if (!chatMsg.trim() || !program || !hqData) return;
    try {
      const res = await fetch(`${API_URL}/api/programs/${program.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          content: chatMsg.trim(),
          team_id: hqData.team.id,
          message_type: broadcast ? 'broadcast' : 'text',
          is_broadcast: broadcast,
        }),
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages(prev => [...prev, msg]);
        setChatMsg('');
        setIsBroadcast(false);
        toast.success(broadcast ? 'Broadcast sent' : 'Message sent');
      }
    } catch { toast.error('Failed to send'); }
  };

  const rsvp = async (eventId, status) => {
    if (!program) return;
    await fetch(`${API_URL}/api/programs/${program.id}/schedule/${eventId}/rsvp?status=${status}`, {
      method: 'POST', headers,
    });
    // Update local RSVP state
    setHqData(prev => {
      if (!prev) return prev;
      const updateEvents = (events) => events.map(e => {
        if (e.id === eventId) {
          return { ...e, rsvps: { ...(e.rsvps || {}), [user?.id]: status } };
        }
        return e;
      });
      return {
        ...prev,
        schedule_upcoming: updateEvents(prev.schedule_upcoming || []),
      };
    });
    toast.success(`RSVP: ${status}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!program || !hqData) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-500 mb-3">Team not found</p>
          <Link to={`/${slug}/teams`} className="text-cyan-400 hover:underline text-sm">Back to Teams</Link>
        </div>
      </div>
    );
  }

  const { team, roster, schedule_upcoming, schedule_past, next_game, recent_results, announcements, media } = hqData;
  const color = program.primary_color || '#06b6d4';
  const colorBg = `${color}15`;
  const canMessage = myRole && ['program_admin', 'super_admin', 'coach', 'parent'].includes(myRole);
  const showMessages = !!token && !!myRole;

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', icon: Layers },
    { id: 'roster', label: 'Roster', icon: Users },
    { id: 'schedule', label: 'Schedule', icon: Calendar },
    ...(showMessages ? [{ id: 'messages', label: 'Messages', icon: MessageCircle }] : []),
    { id: 'media', label: 'Media', icon: Image },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* ====== TEAM HEADER ====== */}
      <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${program.secondary_color || '#0a0a0a'} 0%, #0a0a0a 100%)` }}>
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full blur-[100px] opacity-15" style={{ backgroundColor: color }} />
        <div className="relative max-w-6xl mx-auto px-4 pt-5 pb-6">
          <Link to={`/${slug}/teams`} className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-300 text-xs mb-4 transition-colors" data-testid="back-to-teams">
            <ChevronLeft className="w-3 h-3" /> All Teams
          </Link>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center text-lg font-black text-white shrink-0 shadow-xl" style={{ backgroundColor: color, boxShadow: `0 8px 24px ${color}30` }}>
              {team.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight" data-testid="team-name">{team.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                {team.age_group && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-zinc-700 text-zinc-400">{team.age_group}</Badge>
                )}
                {team.level && (
                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${LEVEL_STYLES[team.level] || ''}`}>
                    <Sparkles className="w-2.5 h-2.5 mr-0.5" />{team.level}
                  </Badge>
                )}
                {team.division && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-zinc-700 text-zinc-500">{team.division}</Badge>
                )}
                {team.record && team.record !== '0-0' && (
                  <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md" style={{ backgroundColor: colorBg }}>
                    <Trophy className="w-3 h-3" style={{ color }} />
                    <span className="text-white font-bold font-mono text-xs">{team.record}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Nav */}
      <div className="border-b border-zinc-800 sticky top-0 z-10 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-0.5 overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id ? 'border-current text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'
                }`}
                style={activeTab === tab.id ? { color } : {}}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" /> {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* ========== DASHBOARD TAB ========== */}
        {activeTab === 'dashboard' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              {/* Next Game */}
              {next_game && (
                <Card className="bg-zinc-900/80 border-zinc-800/60 overflow-hidden" data-testid="next-game-card">
                  <div className="h-0.5" style={{ backgroundColor: color }} />
                  <CardContent className="p-5">
                    <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Next Game</div>
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-16 rounded-xl flex flex-col items-center justify-center" style={{ backgroundColor: colorBg }}>
                        <span className="text-[10px] font-medium uppercase" style={{ color }}>{formatDay(next_game.start_time).weekday}</span>
                        <span className="text-white font-black text-xl leading-none">{formatDay(next_game.start_time).day}</span>
                        <span className="text-[9px] text-zinc-500">{formatDay(next_game.start_time).month}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-bold text-lg">{next_game.title}</h3>
                        <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatTime(next_game.start_time)}</span>
                          {next_game.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {next_game.location}</span>}
                        </div>
                        {next_game.opponent && (
                          <Badge className="mt-2 text-xs px-2 py-0.5 bg-red-500/10 text-red-400 border border-red-800">
                            vs {next_game.opponent}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Results */}
              {recent_results.length > 0 && (
                <Card className="bg-zinc-900/80 border-zinc-800/60" data-testid="recent-results">
                  <CardContent className="p-5">
                    <h2 className="text-white font-bold text-base flex items-center gap-2 mb-3">
                      <Trophy className="w-4 h-4 text-amber-400" /> Recent Results
                    </h2>
                    <div className="space-y-2">
                      {recent_results.map(g => {
                        const isWin = g.result?.startsWith('W');
                        const scoreMatch = g.result?.match(/[WL]\s*(\d+)\s*-\s*(\d+)/);
                        const scoreDisplay = scoreMatch ? `${scoreMatch[1]}-${scoreMatch[2]}` : g.result;
                        return (
                        <div key={g.id} className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-xl bg-zinc-800/40 border border-zinc-800/60">
                          <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center font-black text-xs shrink-0 ${
                            isWin ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'
                          }`}>{isWin ? 'W' : 'L'}</div>
                          <div className="flex-1 min-w-0">
                            <span className="text-white text-xs sm:text-sm font-medium block truncate">{g.title}</span>
                            <div className="text-[10px] text-zinc-500">{formatDate(g.start_time)}</div>
                          </div>
                          <span className="text-white font-bold font-mono text-xs sm:text-sm shrink-0">{scoreDisplay}</span>
                        </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Announcements */}
              {announcements.length > 0 && (
                <Card className="bg-zinc-900/80 border-zinc-800/60 overflow-hidden" data-testid="dash-announcements">
                  <div className="h-0.5 bg-amber-500" />
                  <CardContent className="p-4">
                    <h3 className="text-white font-bold text-sm flex items-center gap-2 mb-3">
                      <Megaphone className="w-4 h-4 text-amber-400" /> Announcements
                    </h3>
                    <div className="space-y-2.5">
                      {announcements.slice(0, 3).map(a => (
                        <div key={a.id} className={`p-2.5 rounded-lg border ${a.priority === 'urgent' ? 'bg-red-500/[0.06] border-red-900/40' : 'bg-zinc-800/40 border-zinc-800/60'}`}>
                          <div className="flex items-start gap-1.5 mb-1">
                            {a.priority === 'urgent' ? <AlertTriangle className="w-3 h-3 text-red-400 shrink-0 mt-0.5" /> : <Star className="w-3 h-3 text-amber-400 shrink-0 mt-0.5" />}
                            <span className="text-white text-xs font-semibold">{a.title}</span>
                          </div>
                          <p className="text-zinc-400 text-[10px] leading-relaxed line-clamp-3">{a.content}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Quick Roster */}
              <Card className="bg-zinc-900/80 border-zinc-800/60" data-testid="dash-roster">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-bold text-sm flex items-center gap-2">
                      <Users className="w-4 h-4" style={{ color }} /> Roster ({roster.length})
                    </h3>
                    <button onClick={() => setActiveTab('roster')} className="text-xs hover:underline" style={{ color }}>View All</button>
                  </div>
                  <div className="space-y-1.5">
                    {roster.slice(0, 5).map(m => (
                      <div key={m.id} className="flex items-center gap-2 py-1">
                        <div className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold" style={{ backgroundColor: colorBg, color }}>
                          {m.jersey_number ? `${m.jersey_number}` : m.name?.[0]}
                        </div>
                        <span className="text-xs text-white flex-1 truncate">{m.name}</span>
                        <span className="text-[10px] text-zinc-500">{m.position || m.role}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ========== ROSTER TAB ========== */}
        {activeTab === 'roster' && (
          <div data-testid="roster-tab">
            <h2 className="text-white font-bold text-lg mb-4">Team Roster ({roster.length})</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {roster.map(m => (
                <Card key={m.id} className="bg-zinc-900/80 border-zinc-800/60" data-testid={`roster-member-${m.id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center font-bold text-sm shrink-0" style={{ backgroundColor: `${color}20`, color }}>
                      {m.jersey_number ? <><Hash className="w-3 h-3 opacity-50" />{String(m.jersey_number).replace(/^#/, '')}</> : m.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold text-sm truncate">{m.name}</div>
                      <div className="flex items-center gap-2 text-[10px] mt-0.5">
                        {m.position && <span className="text-zinc-400">{m.position}</span>}
                        <Badge variant="outline" className={`text-[9px] px-1 py-0 ${
                          m.role === 'coach' ? 'text-cyan-400 border-cyan-800' : 'text-zinc-500 border-zinc-700'
                        }`}>{m.role}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* ========== SCHEDULE TAB ========== */}
        {activeTab === 'schedule' && (
          <div data-testid="schedule-tab">
            {/* Upcoming */}
            <h2 className="text-white font-bold text-lg mb-3">Upcoming</h2>
            {schedule_upcoming.length === 0 ? (
              <p className="text-zinc-600 text-sm mb-6">No upcoming events</p>
            ) : (
              <div className="space-y-2 mb-8">
                {schedule_upcoming.map(event => {
                  const d = formatDay(event.start_time);
                  const myRsvp = event.rsvps?.[user?.id];
                  const summary = rsvpSummaries[event.id];
                  return (
                    <Card key={event.id} className="bg-zinc-900/80 border-zinc-800/60" data-testid={`schedule-${event.id}`}>
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                          <div className="w-12 h-14 rounded-lg flex flex-col items-center justify-center shrink-0" style={{ backgroundColor: colorBg }}>
                            <span className="text-[10px] font-medium uppercase" style={{ color }}>{d.weekday}</span>
                            <span className="text-white font-black text-lg leading-none">{d.day}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-semibold text-sm">{event.title}</span>
                              <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${
                                event.event_type === 'game' ? 'text-red-400 border-red-800' :
                                event.event_type === 'tournament' ? 'text-amber-400 border-amber-800' :
                                'text-zinc-500 border-zinc-700'
                              }`}>{event.event_type.replace('_', ' ')}</Badge>
                            </div>
                            <div className="flex items-center gap-3 text-[11px] text-zinc-500 mt-1">
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatTime(event.start_time)}</span>
                              {event.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {event.location}</span>}
                              {event.opponent && <span>vs <span className="text-white">{event.opponent}</span></span>}
                            </div>
                          </div>
                          {/* RSVP Buttons */}
                          {token && (
                            <div className="flex gap-1 shrink-0">
                              {[
                                { status: 'yes', icon: CheckCircle2, active: 'bg-emerald-500/20 text-emerald-400 border-emerald-700', label: 'Going' },
                                { status: 'maybe', icon: HelpCircle, active: 'bg-amber-500/20 text-amber-400 border-amber-700', label: 'Maybe' },
                                { status: 'no', icon: XCircle, active: 'bg-red-500/20 text-red-400 border-red-700', label: 'Not Going' },
                              ].map(r => (
                                <button
                                  key={r.status}
                                  onClick={() => rsvp(event.id, r.status)}
                                  className={`px-2 py-1 rounded-md text-[10px] font-medium border transition-colors flex items-center gap-1 ${
                                    myRsvp === r.status ? r.active : 'bg-zinc-800/50 text-zinc-500 border-zinc-800 hover:border-zinc-700'
                                  }`}
                                  data-testid={`rsvp-${r.status}-${event.id}`}
                                >
                                  <r.icon className="w-3 h-3" /> {r.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* RSVP Summary for Coaches/Admins */}
                        {isCoachOrAdmin && summary && (
                          <div className="mt-3 pt-3 border-t border-zinc-800/60" data-testid={`rsvp-summary-${event.id}`}>
                            <div className="flex items-center gap-4 text-[11px]">
                              <span className="text-zinc-500 font-medium flex items-center gap-1">
                                <Users className="w-3 h-3" /> RSVP Summary:
                              </span>
                              <span className="flex items-center gap-1 text-emerald-400">
                                <CheckCircle2 className="w-3 h-3" /> {summary.counts.going} Going
                              </span>
                              <span className="flex items-center gap-1 text-amber-400">
                                <HelpCircle className="w-3 h-3" /> {summary.counts.maybe} Maybe
                              </span>
                              <span className="flex items-center gap-1 text-red-400">
                                <XCircle className="w-3 h-3" /> {summary.counts.not_going} Not Going
                              </span>
                            </div>
                            {(summary.going.length > 0 || summary.maybe.length > 0 || summary.not_going.length > 0) && (
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                                {summary.going.length > 0 && (
                                  <div className="text-[10px]">
                                    <span className="text-emerald-500 font-medium">Going: </span>
                                    <span className="text-zinc-400">{summary.going.map(u => u.name).join(', ')}</span>
                                  </div>
                                )}
                                {summary.maybe.length > 0 && (
                                  <div className="text-[10px]">
                                    <span className="text-amber-500 font-medium">Maybe: </span>
                                    <span className="text-zinc-400">{summary.maybe.map(u => u.name).join(', ')}</span>
                                  </div>
                                )}
                                {summary.not_going.length > 0 && (
                                  <div className="text-[10px]">
                                    <span className="text-red-500 font-medium">Not Going: </span>
                                    <span className="text-zinc-400">{summary.not_going.map(u => u.name).join(', ')}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Past / Completed */}
            {schedule_past.length > 0 && (
              <>
                <h2 className="text-white font-bold text-lg mb-3">Past Games</h2>
                <div className="space-y-2">
                  {schedule_past.map(event => (
                    <Card key={event.id} className="bg-zinc-900/80 border-zinc-800/60 opacity-70" data-testid={`past-${event.id}`}>
                      <CardContent className="p-3 flex items-center gap-3">
                        {event.result ? (
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-black text-xs ${
                            event.result === 'W' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'
                          }`}>{event.result}</div>
                        ) : (
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-zinc-800 text-zinc-600 text-xs">—</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <span className="text-white text-sm font-medium">{event.title}</span>
                          <div className="text-[10px] text-zinc-500">{formatDate(event.start_time)} {event.location && `· ${event.location}`}</div>
                        </div>
                        {event.home_score !== undefined && (
                          <span className="text-white font-bold font-mono text-sm">{event.home_score}-{event.away_score}</span>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ========== MESSAGES TAB ========== */}
        {activeTab === 'messages' && showMessages && (
          <div className="max-w-2xl" data-testid="messages-tab">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-zinc-800">
                <h2 className="text-white font-semibold text-sm flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" style={{ color }} /> Team Messages
                </h2>
                <p className="text-zinc-500 text-[10px] mt-0.5">
                  {isCoachOrAdmin ? 'Send messages or broadcasts (one-way announcements).' : 'View team messages and reply.'}
                </p>
              </div>
              <div className="p-4 space-y-3 max-h-[55vh] overflow-y-auto" data-testid="messages-list">
                {messages.length === 0 ? (
                  <p className="text-zinc-600 text-sm text-center py-8">No messages yet</p>
                ) : (
                  messages.map(msg => {
                    const isBroadcastMsg = msg.is_broadcast || msg.message_type === 'broadcast';
                    return (
                      <div
                        key={msg.id}
                        className={`flex gap-3 ${isBroadcastMsg ? 'p-3 rounded-xl border border-amber-900/40 bg-amber-500/[0.04]' : ''}`}
                        data-testid={`msg-${msg.id}`}
                      >
                        {isBroadcastMsg ? (
                          <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
                            <Megaphone className="w-4 h-4 text-amber-400" />
                          </div>
                        ) : (
                          <Avatar className="h-8 w-8 shrink-0">
                            <AvatarFallback className="text-xs bg-zinc-800 text-zinc-400">{msg.sender_name?.[0]?.toUpperCase()}</AvatarFallback>
                          </Avatar>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-white">{msg.sender_name}</span>
                            <Badge variant="outline" className={`text-[8px] px-1 py-0 ${
                              msg.sender_role === 'coach' || msg.sender_role === 'program_admin' || msg.sender_role === 'super_admin'
                                ? 'text-cyan-400 border-cyan-800' : 'text-zinc-500 border-zinc-700'
                            }`}>{msg.sender_role === 'program_admin' ? 'Admin' : msg.sender_role === 'super_admin' ? 'Staff' : msg.sender_role}</Badge>
                            {isBroadcastMsg && (
                              <Badge className="text-[8px] px-1.5 py-0 bg-amber-500/15 text-amber-400 border border-amber-800">
                                <Megaphone className="w-2.5 h-2.5 mr-0.5" /> Broadcast
                              </Badge>
                            )}
                            <span className="text-[10px] text-zinc-600">{formatDate(msg.created_at)} {formatTime(msg.created_at)}</span>
                          </div>
                          <p className={`text-sm mt-0.5 ${isBroadcastMsg ? 'text-zinc-200 font-medium' : 'text-zinc-300'}`}>{msg.content}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {canMessage && (
                <div className="border-t border-zinc-800 p-3">
                  {/* Broadcast toggle for coaches/admins */}
                  {isCoachOrAdmin && (
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={() => setIsBroadcast(!isBroadcast)}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-colors ${
                          isBroadcast
                            ? 'bg-amber-500/15 text-amber-400 border-amber-800'
                            : 'bg-zinc-800/50 text-zinc-500 border-zinc-800 hover:border-zinc-700'
                        }`}
                        data-testid="broadcast-toggle"
                      >
                        <Megaphone className="w-3 h-3" />
                        {isBroadcast ? 'Broadcast ON' : 'Broadcast'}
                      </button>
                      {isBroadcast && (
                        <span className="text-[10px] text-amber-500/70">One-way announcement — no replies</span>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      value={chatMsg}
                      onChange={e => setChatMsg(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && sendMessage(isBroadcast)}
                      placeholder={isBroadcast ? 'Type a broadcast...' : (isCoachOrAdmin ? 'Post a message...' : 'Reply...')}
                      className="bg-zinc-800 border-zinc-700 text-white flex-1"
                      data-testid="team-chat-input"
                    />
                    <Button
                      onClick={() => sendMessage(isBroadcast)}
                      style={{ backgroundColor: isBroadcast ? '#d97706' : color }}
                      data-testid="team-chat-send"
                    >
                      {isBroadcast ? <Megaphone className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========== MEDIA TAB ========== */}
        {activeTab === 'media' && (
          <div data-testid="media-tab">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold text-lg">Media</h2>
              <Link
                to={`/${slug}/media`}
                className="text-xs font-medium hover:underline" style={{ color }}
                data-testid="view-all-media-link"
              >
                View All Program Media
              </Link>
            </div>
            {media.length === 0 ? (
              <div className="text-center py-16">
                <Image className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">No media uploaded yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {media.map(m => {
                  const catMeta = {
                    highlight: { label: 'Highlight', icon: Film, cls: 'text-amber-400 border-amber-800 bg-amber-500/10' },
                    full_game: { label: 'Full Game', icon: Video, cls: 'text-cyan-400 border-cyan-800 bg-cyan-500/10' },
                    photo: { label: 'Photo', icon: Camera, cls: 'text-emerald-400 border-emerald-800 bg-emerald-500/10' },
                  };
                  const cat = catMeta[m.category] || catMeta.photo;
                  const CatIcon = cat.icon;
                  const isVideo = m.media_type === 'video';
                  const thumbUrl = m.thumbnail_url ? (m.thumbnail_url.startsWith('http') ? m.thumbnail_url : `${API_URL}${m.thumbnail_url}`) : null;
                  const mediaUrl = m.url?.startsWith('http') ? m.url : `${API_URL}${m.url}`;

                  return (
                    <div
                      key={m.id}
                      className="group cursor-pointer rounded-xl overflow-hidden bg-zinc-900/80 border border-zinc-800/60 hover:border-zinc-700 transition-all"
                      data-testid={`media-item-${m.id}`}
                    >
                      <div className="relative aspect-video bg-zinc-800 overflow-hidden">
                        {thumbUrl ? (
                          <img src={thumbUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                        ) : !isVideo ? (
                          <img src={mediaUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-10 h-10 rounded-full bg-white/10 group-hover:bg-white/20 flex items-center justify-center transition-colors">
                              <Play className="w-4 h-4 text-white ml-0.5" />
                            </div>
                          </div>
                        )}
                        <Badge className={`absolute top-2 left-2 text-[8px] px-1.5 py-0 border backdrop-blur-sm ${cat.cls}`}>
                          <CatIcon className="w-2.5 h-2.5 mr-0.5" /> {cat.label}
                        </Badge>
                        {isVideo && thumbUrl && (
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                              <Play className="w-4 h-4 text-white ml-0.5" />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="p-2.5">
                        <h3 className="text-white text-xs font-medium line-clamp-1">{m.title || 'Untitled'}</h3>
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-500">
                          {m.event_name && <span className="truncate max-w-[120px]">{m.event_name}</span>}
                        </div>
                        <span className="text-[10px] text-zinc-600 block mt-0.5">{formatDate(m.created_at)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamHQ;
